import { useState } from "react";
import { CheckCircle2, ChevronDown, Loader2, Lock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { TEACHER_SCENARIO_STAGES, type TeacherScenarioMode, type TeacherScenarioStage } from "@/config/teacherScenarios";

const STAGE_ORDER: TeacherScenarioStage[] = ["setup", "plan", "live", "debrief"];

export function getVisibleStages(mode: TeacherScenarioMode): typeof TEACHER_SCENARIO_STAGES {
  if (mode === "prep") {
    return TEACHER_SCENARIO_STAGES.filter((s) => s.id !== "live");
  }
  return TEACHER_SCENARIO_STAGES;
}

export function stageIndex(stage: TeacherScenarioStage, mode: TeacherScenarioMode): number {
  const visible = getVisibleStages(mode);
  return visible.findIndex((s) => s.id === stage);
}

export function isStageReachable(
  target: TeacherScenarioStage,
  active: TeacherScenarioStage,
  mode: TeacherScenarioMode,
): boolean {
  const visible = getVisibleStages(mode);
  const targetIdx = visible.findIndex((s) => s.id === target);
  const activeIdx = visible.findIndex((s) => s.id === active);
  return targetIdx <= activeIdx;
}

interface ScenarioStageTrackerProps {
  mode: TeacherScenarioMode;
  activeStage: TeacherScenarioStage;
  reachedStages: Record<TeacherScenarioStage, boolean>;
  onStageSelect?: (stage: TeacherScenarioStage) => void;
  onSwitchMode?: () => void;
  switchModeDisabled?: boolean;
  isSwitchingMode?: boolean;
}

export const ScenarioStageTracker = ({
  mode,
  activeStage,
  reachedStages,
  onStageSelect,
  onSwitchMode,
  switchModeDisabled = false,
  isSwitchingMode = false,
}: ScenarioStageTrackerProps) => {
  const [expanded, setExpanded] = useState(false);
  const stages = getVisibleStages(mode);
  const activeMeta = stages.find((s) => s.id === activeStage);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="rounded-xl border border-bodhi-gold/30 bg-white px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2 text-left">
            <p className="shrink-0 font-inter text-[11px] font-semibold uppercase tracking-wide text-bodhi-teal/70">
              Progress
            </p>
            <div className="min-w-0 flex-1">
              <p className="truncate font-inter text-xs font-medium text-bodhi-teal">
                {activeMeta?.label ?? activeStage}
                <span className="font-normal text-gray-500"> · {activeMeta?.micro}</span>
              </p>
            </div>
            <ChevronDown
              className={cn("h-4 w-4 shrink-0 text-bodhi-teal/60 transition-transform", expanded && "rotate-180")}
            />
          </CollapsibleTrigger>

          <div className="flex shrink-0 items-center gap-1.5">
            {onSwitchMode && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={switchModeDisabled || isSwitchingMode}
                onClick={onSwitchMode}
                className="h-7 border-bodhi-teal/30 px-2 font-inter text-[11px] text-bodhi-teal hover:bg-bodhi-teal/5"
              >
                {isSwitchingMode ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1 h-3 w-3" />
                )}
                {mode === "prep" ? "Switch to Practice" : "Switch to Prep"}
              </Button>
            )}
          </div>
        </div>

        <CollapsibleContent>
          <p className="mb-2 mt-1 font-inter text-[11px] capitalize text-gray-400">{mode} mode · tap a step to revisit</p>
          <ol className="flex flex-wrap gap-1.5">
            {stages.map((stage, idx) => {
              const reached = reachedStages[stage.id];
              const active = stage.id === activeStage;
              const locked = !reached && !active;
              const navigable = Boolean(onStageSelect) && (reached || active);

              const pillClass = cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-inter text-xs transition-colors",
                active && "border-bodhi-orange bg-bodhi-orange/10 font-medium text-bodhi-teal",
                reached && !active && "border-bodhi-teal/25 bg-bodhi-teal/5 text-gray-700",
                locked && "border-gray-100 bg-gray-50 text-gray-400",
                navigable && !active && "cursor-pointer hover:border-bodhi-teal/40 hover:bg-bodhi-teal/10",
              );

              const icon = reached ? (
                <CheckCircle2 className="h-3 w-3 shrink-0 text-bodhi-teal" />
              ) : locked ? (
                <Lock className="h-3 w-3 shrink-0" />
              ) : (
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-bodhi-orange text-[9px] font-bold text-white">
                  {idx + 1}
                </span>
              );

              if (navigable) {
                return (
                  <li key={stage.id}>
                    <button
                      type="button"
                      onClick={() => onStageSelect?.(stage.id)}
                      className={pillClass}
                      aria-current={active ? "step" : undefined}
                      title={stage.micro}
                    >
                      {icon}
                      {stage.label}
                    </button>
                  </li>
                );
              }

              return (
                <li key={stage.id} className={pillClass} aria-current={active ? "step" : undefined} title={stage.micro}>
                  {icon}
                  {stage.label}
                </li>
              );
            })}
          </ol>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export { STAGE_ORDER };
